import {
  HttpClient,
  HttpHeaders,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { APP_CONFIG, getApiPath } from '@core/configs';
import { ApiResponse } from '@core/models/interfaces';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { LOG_RESULT, LOG_STATUS, LOG_TYPE, NativeDiagnosticLogService } from '@core/services/diagnostic-log/native-diagnostic-log.service';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  protected _http: HttpClient;
  constructor(
    protected readonly http: HttpClient,
    private readonly nativeDiagnosticLogService: NativeDiagnosticLogService,
  ) {
      this._http = http;
   }

  /**
   * Wraps an API request with a generic diagnostic log — one entry when the call starts,
   * one when it ends (success or error).
   */
  private withApiLog<T>(apiUrl: string, source$: Observable<ApiResponse<T>>): Observable<ApiResponse<T>> {
    const logId = this.nativeDiagnosticLogService.generateLogId();
    const subject = getApiPath(apiUrl);
    this.nativeDiagnosticLogService.sendCareMoniLog({
      logType: LOG_TYPE.API,
      subject,
      status: LOG_STATUS.START,
      message: LOG_RESULT.OK,
      logId,
    });

    let endStatus: string | number = LOG_STATUS.UNDEFINED;
    let endMessage: string = LOG_RESULT.ERROR;

    return source$.pipe(
      tap((res) => {
        endStatus = res?.status ?? LOG_STATUS.OK;
        endMessage = LOG_RESULT.SUCCESS;
      }),
      catchError((err) => {
        endStatus = err?.status ?? LOG_STATUS.UNDEFINED;
        endMessage = err?.message ? `${LOG_RESULT.ERROR} ${err.message}` : LOG_RESULT.ERROR;
        return throwError(() => err);
      }),
      finalize(() => {
        this.nativeDiagnosticLogService.sendCareMoniLog({
          logType: LOG_TYPE.API,
          subject,
          status: endStatus,
          message: endMessage,
          logId,
        });
      }),
    );
  }

  /**
   * GET request
   * Ex: [param1, param2, param3]
   * => apiUrl/param1/param2/param3
   */
  get<T = any>(apiUrl: string, parameter: any[] = []): Observable<ApiResponse<T>> {
    parameter.forEach(p => {
      apiUrl += ('/' + p);
    });

    return this.withApiLog(apiUrl, this.http.get<T>(apiUrl, {
      headers: this.getHeaders(),
      observe: 'response',
    }).pipe(
      map(this.forwardData),
      catchError((e) => throwError(() => e)),
    ));
  }

  /**
   * GET request with query
   * Ex: { param1: value1, param2: value2, param3: value3 }
   * => apiUrl?param1=value1&param2=value2&param3=value3
   */
  getWithQuery<T = any>(
    apiUrl: string,
    parameter: { [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean> },
  ): Observable<ApiResponse<T>> {
    return this.withApiLog(apiUrl, this.http.get<T>(apiUrl, {
      headers: this.getHeaders(),
      params: this.getParams(parameter),
      observe: 'response',
    }).pipe(
      map(this.forwardData),
      catchError((e) => throwError(() => e)),
    ));
  }

  /**
   * POST request for callers that implement retry or error-specific behavior.
   */
  postSilent<T = any>(apiUrl: string, body?: any): Observable<ApiResponse<T>> {
    return this.withApiLog(apiUrl, this.http.post<T>(apiUrl, body || {}, {
      headers: this.getHeaders(),
      observe: 'response',
    }).pipe(
      map(this.forwardData),
      catchError((e) => throwError(() => e)),
    ));
  }

  /**
   * POST request
   */
  post<T = any>(
    apiUrl: string,
    body?: any,
  ): Observable<ApiResponse<T>> {
    return this.withApiLog(apiUrl, this.http.post<T>(
      apiUrl,
      body || {},
      {
        headers: this.getHeaders(),
        observe: 'response',
      },
    ).pipe(
      map(this.forwardData),
      catchError((e) => throwError(() => e)),
    ));
  }

  postText(
    apiUrl: string,
    body?: any,
  ): Observable<ApiResponse<string>> {
    return this.withApiLog(apiUrl, this.http.post(
      apiUrl,
      body || {},
      {
        headers: this.getHeaders(),
        observe: 'response',
        responseType: 'text'
      },
    ).pipe(
      map(this.forwardData),
      catchError((e) => throwError(() => e)),
    ));
  }

  /**
   * POST formdata request
   */
  postFormData<T = any>(
    apiUrl: string,
    body?: any,
  ): Observable<ApiResponse<T>> {
    return this.withApiLog(apiUrl, this.http.post<T>(
      apiUrl,
      body,
      {
        headers: this.getHeadersFormData(),
        observe: 'response',
      },
    ).pipe(
      map(this.forwardData),
      catchError((e) => throwError(() => e)),
    ));
  }

  /**
   * POST request with query
   */
  postWithQuery<T = any>(
    apiUrl: string,
    parameter: { [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean> },
    body?: any,
  ): Observable<ApiResponse<T>> {
    return this.withApiLog(apiUrl, this.http.post<T>(
      apiUrl,
      body || {},
      {
        headers: this.getHeaders(),
        params: this.getParams(parameter),
        observe: 'response',
      },
    ).pipe(
      map(this.forwardData),
      catchError((e) => throwError(() => e)),
    ));
  }

  /**
   * PUT request
   */
  put<T = any>(apiUrl: string, body?: any): Observable<ApiResponse<T>> {
    return this.withApiLog(apiUrl, this.http.put<T>(
      apiUrl,
      body || {},
      {
        headers: this.getHeaders(),
        observe: 'response',
      },
    ).pipe(
      map(this.forwardData),
      catchError((e) => throwError(() => e)),
    ));
  }

  /**
   * PUT formdata request
   */
  putFormData<T = any>(
    apiUrl: string,
    body?: any,
  ): Observable<ApiResponse<T>> {
    return this.withApiLog(apiUrl, this.http.put<T>(
      apiUrl,
      body,
      {
        headers: this.getHeadersFormData(),
        observe: 'response',
      },
    ).pipe(
      map(this.forwardData),
      catchError((e) => throwError(() => e)),
    ));
  }

  /**
   * DELETE request
   */
  delete<T = any>(apiUrl: string, data?: any | any[]): Observable<ApiResponse<T>> {
    if (data) {
      if (Array.isArray(data)) {
        data.forEach((p: string) => {
          apiUrl += ('/' + p);
        });
      } else if (typeof data === 'object') {
        return this.withApiLog(apiUrl, this.http.request<T>(
          'delete',
          apiUrl,
          {
            body: data || {},
            headers: this.getHeaders(),
            observe: 'response',
          },
        ).pipe(
          map(this.forwardData),
          catchError((e) => throwError(() => e)),
        ));
      } else {
        apiUrl += ('/' + data);
      }
    }
    return this.withApiLog(apiUrl, this.http.delete<T>(
      apiUrl,
      {
        headers: this.getHeaders(),
        observe: 'response',
      },
    ).pipe(
      map(this.forwardData),
      catchError((e) => throwError(() => e)),
    ));
  }

  /**
   * DELETE request with query
   */
  deleteWithQuery<T = any>(
    apiUrl: string,
    parameter: { [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean> },
    data?: any | any[],
  ): Observable<ApiResponse<T>> {
    if (data) {
      if (Array.isArray(data)) {
        data.forEach((p: string) => {
          apiUrl += ('/' + p);
        });
      } else if (typeof data === 'object') {
        return this.withApiLog(apiUrl, this.http.request<T>(
          'delete',
          apiUrl,
          {
            body: data || {},
            headers: this.getHeaders(),
            params: this.getParams(parameter),
            observe: 'response',
          },
        ).pipe(
          map(this.forwardData),
          catchError((e) => throwError(() => e)),
        ));
      } else {
        apiUrl += ('/' + data);
      }
    }
    return this.withApiLog(apiUrl, this.http.delete<T>(
      apiUrl,
      {
        headers: this.getHeaders(),
        params: this.getParams(parameter),
        observe: 'response',
      },
    ).pipe(
      map(this.forwardData),
      catchError((e) => throwError(() => e)),
    ));
  }

  /**
   * Forward data
   */
  private forwardData<T = any>(res: HttpResponse<T>): ApiResponse<T> {
    return { result: res.body, status: res.status, message: 'successful' };
  }

  /**
   * Get HttpParams from object
   */
  private getParams(obj: {
    [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>;
  }): HttpParams {
    return new HttpParams({ fromObject: obj });
  }

  /**
   * Common header for each API
   */
  private getHeaders(): HttpHeaders {
    return new HttpHeaders(
      {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'content-type': 'application/json',
        // eslint-disable-next-line quote-props
        'module': APP_CONFIG.module,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'x-requested-with': 'XMLHttpRequest',
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'access-control-allow-origin': '*',
      },
    );
  }

  /**
     * Funtion get: Get data xml local by xmlpath
     * @param apiUrl
     */
  getXML(apiUrl: string = '') {
    return new Promise((resolve, reject) => {
        this.http.get(apiUrl, {
            headers: this.getHeaders(),
            observe: 'response',
            responseType: 'text'
        }).subscribe(
            (data) => {
                resolve(data);
            },
            (err) => {
                console.log(err);
                resolve(err);
            }
        );
    });
  }

  /**
   * Common header for each API
   */
  private getHeadersFormData(): HttpHeaders {
    return new HttpHeaders(
      {
        // eslint-disable-next-line quote-props
        'module': APP_CONFIG.module,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'x-requested-with': 'XMLHttpRequest',
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'access-control-allow-origin': '*',
      },
    );
  }

  postFile(
    apiUrl: string,
    body?: any
  ): Observable<Blob> {
    return this.http.post(
      apiUrl,
      body || {},
      {
        headers: this.getHeaders(),
        responseType: 'blob', // <-- important
        observe: 'response',
      }
    ).pipe(
      map(resp => resp.body as Blob),
      catchError(e => { throw e; })
    );
  }
}
